import React, { useEffect, useState, useRef } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { SurveyProvider } from "./context/SurveyContext";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import ProtectedRoute from "./components/ProtectedRoute";
import RoleProtectedRoute from "./components/RoleProtectedRoute";
import {
  SidebarProvider,
  SidebarInset,
  SidebarTrigger,
} from "./components/ui/sidebar";
import { AppSidebar } from "./components/AppSidebar";
import { AppBreadcrumb } from "./components/AppBreadcrumb";
import authService from "./services/authService";
import Login from "./components/Login";
import Signup from "./components/Signup";
import Dashboard from "./components/Dashboard-shadcn";
import SurveyList from "./components/SurveyList";
import SurveyBuilder from "./components/SurveyBuilder";
import SurveyResponse from "./components/SurveyResponse";
import SurveyResponses from "./components/SurveyResponses";
import SurveyAnalytics from "./components/SurveyAnalytics";
import Statistics from "./components/Statistics";
import StatisticsData from "./components/StatisticsData";
import UserManagement from "./components/UserManagement";
import SurveyTypeManagement from "./components/SurveyTypeManagement";
import TemplatePage from "./components/TemplatePage";
import { UserRole } from "./types/User";
import {
  Sun,
  Moon,
  Monitor,
  ChevronUp,
  ChevronDown,
  User,
  LogOut,
} from "lucide-react";
import { ENV, validateEnvironment, logConfiguration } from "./config/env";
import { testAPIHeaders } from "./utils/apiTest";
import { browserEnvironmentTest } from "./utils/browserTest";
import { directCheck } from "./utils/directEnvTest";
// import { checkKeys } from "./utils/checkKeys";
import "./App.css";

function AppContent() {
  const navigate = useNavigate();
  const {
    user,
    logout,
    isAuthenticated,
    centerInfo,
    selectedCenterId,
    setSelectedCenterId,
  } = useAuth();
  const { mode, setMode, actualMode } = useTheme();
  const location = useLocation();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [themeMenuOpen, setThemeMenuOpen] = useState(false);
  const [allCenters, setAllCenters] = useState<any[]>([]);
  const [centersLoading, setCentersLoading] = useState(false);
  const [centerSearchTerm, setCenterSearchTerm] = useState("");
  const [isCenterDropdownOpen, setIsCenterDropdownOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const centerDropdownRef = useRef<HTMLDivElement>(null);
  const themeMenuRef = useRef<HTMLDivElement>(null);

  // Check if current route is a public route (doesn't require navigation)
  const isPublicRoute =
    ["/login", "/signup"].includes(location.pathname) ||
    location.pathname.startsWith("/survey/");

  const handleLogout = () => {
    logout();
    navigate("/login");
    setUserMenuOpen(false);
  };

  const handleProfile = () => {
    console.log("Navigate to profile");
    setUserMenuOpen(false);
  };

  // Load all centers for all authenticated users
  useEffect(() => {
    const loadAllCenters = async () => {
      setCentersLoading(true);
      try {
        const result = await authService.getAllCenters();
        if (result.success && result.data) {
          setAllCenters(result.data);
          if (!selectedCenterId) {
            const currentCenterId =
              localStorage.getItem("centerId") || user?.centerId;
            if (currentCenterId) {
              setSelectedCenterId(currentCenterId);
            }
          }
        } else {
          console.error("Failed to load centers:", result.message);
        }
      } catch (error) {
        console.error("Error loading centers:", error);
      } finally {
        setCentersLoading(false);
      }
    };

    if (isAuthenticated && user) {
      loadAllCenters();
    }
  }, [user, isAuthenticated, selectedCenterId, setSelectedCenterId]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target as Node)
      ) {
        setUserMenuOpen(false);
      }
      if (
        centerDropdownRef.current &&
        !centerDropdownRef.current.contains(event.target as Node)
      ) {
        setIsCenterDropdownOpen(false);
      }
      if (
        themeMenuRef.current &&
        !themeMenuRef.current.contains(event.target as Node)
      ) {
        setThemeMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleCenterChange = (centerId: string) => {
    setSelectedCenterId(centerId);
    console.log("Center changed to:", centerId);
    setIsCenterDropdownOpen(false);
    setCenterSearchTerm("");
  };

  // Group centers by server group
  const groupedCenters = allCenters.reduce((acc, center) => {
    const group = center.serverGroup || "기타";
    if (!acc[group]) {
      acc[group] = [];
    }
    acc[group].push(center);
    return acc;
  }, {} as Record<string, any[]>);

  // Filter centers based on search term
  const getFilteredCenters = () => {
    const filtered: Record<string, any[]> = {};
    const sortedGroups = Object.keys(groupedCenters).sort((a, b) => {
      const getGroupNumber = (group: string) => {
        const match = group.match(/G(\d+)/);
        return match ? parseInt(match[1]) : 999;
      };
      const numA = getGroupNumber(a);
      const numB = getGroupNumber(b);
      if (numA !== numB) {
        return numA - numB;
      }
      return a.localeCompare(b);
    });

    sortedGroups.forEach((group) => {
      const centers = groupedCenters[group];
      const filteredCenters = (centers as any[]).filter((center: any) =>
        center.label.toLowerCase().includes(centerSearchTerm.toLowerCase())
      );
      if (filteredCenters.length > 0) {
        filtered[group] = filteredCenters;
      }
    });
    return filtered;
  };

  const selectedCenter = allCenters.find((c) => c.id === selectedCenterId);

  // For public routes, render without sidebar
  if (!isAuthenticated || isPublicRoute) {
    return (
      <div className="min-h-screen bg-background">
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/survey/:id" element={<SurveyResponse />} />

          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Navigate to="/dashboard" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/survey-list"
            element={
              <ProtectedRoute>
                <SurveyList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/survey-builder"
            element={
              <RoleProtectedRoute
                allowedRoles={[UserRole.SYSTEM_ADMIN, UserRole.GENERAL_ADMIN]}
              >
                <SurveyBuilder />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/templates"
            element={
              <RoleProtectedRoute
                allowedRoles={[UserRole.SYSTEM_ADMIN, UserRole.GENERAL_ADMIN]}
              >
                <TemplatePage />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/create"
            element={
              <RoleProtectedRoute
                allowedRoles={[UserRole.SYSTEM_ADMIN, UserRole.GENERAL_ADMIN]}
              >
                <SurveyBuilder />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/edit/:id"
            element={
              <RoleProtectedRoute
                allowedRoles={[UserRole.SYSTEM_ADMIN, UserRole.GENERAL_ADMIN]}
              >
                <SurveyBuilder />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/survey-responses"
            element={
              <RoleProtectedRoute
                allowedRoles={[UserRole.SYSTEM_ADMIN, UserRole.GENERAL_ADMIN]}
              >
                <SurveyResponses />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/analytics"
            element={
              <RoleProtectedRoute
                allowedRoles={[UserRole.SYSTEM_ADMIN, UserRole.GENERAL_ADMIN]}
              >
                <SurveyAnalytics />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/analytics/:id"
            element={
              <RoleProtectedRoute
                allowedRoles={[UserRole.SYSTEM_ADMIN, UserRole.GENERAL_ADMIN]}
              >
                <SurveyAnalytics />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/statistics"
            element={
              <RoleProtectedRoute
                allowedRoles={[UserRole.SYSTEM_ADMIN, UserRole.GENERAL_ADMIN]}
              >
                <Statistics />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/user-management"
            element={
              <RoleProtectedRoute allowedRoles={[UserRole.SYSTEM_ADMIN]}>
                <UserManagement />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/management/survey-types"
            element={
              <RoleProtectedRoute
                allowedRoles={[UserRole.SYSTEM_ADMIN, UserRole.GENERAL_ADMIN]}
              >
                <SurveyTypeManagement />
              </RoleProtectedRoute>
            }
          />
        </Routes>
      </div>
    );
  }

  // For authenticated routes, use sidebar layout
  return (
    <SidebarProvider defaultOpen={true}>
      <div className="flex min-h-screen w-full">
        <AppSidebar />
        <SidebarInset className="flex-1">
          {/* Header with trigger button */}
          <header className="sticky top-0 z-10 flex h-16 items-center gap-4 border-b bg-background px-6">
            <SidebarTrigger className="-ml-2" />
            <div className="flex-1">
              <AppBreadcrumb />
            </div>
            <div className="flex items-center gap-4">
              {/* Center Selection for System Admin */}
              {user?.role === UserRole.SYSTEM_ADMIN && (
                <div className="relative" ref={centerDropdownRef}>
                  {centersLoading ? (
                    <span className="text-sm text-muted-foreground">
                      로딩 중...
                    </span>
                  ) : (
                    <div>
                      <button
                        className="flex items-center gap-2 px-3 py-1.5 text-sm border rounded-md hover:bg-accent"
                        onClick={() =>
                          setIsCenterDropdownOpen(!isCenterDropdownOpen)
                        }
                      >
                        <span>
                          {selectedCenter ? selectedCenter.label : "센터 선택"}
                        </span>
                        {isCenterDropdownOpen ? (
                          <ChevronUp size={16} />
                        ) : (
                          <ChevronDown size={16} />
                        )}
                      </button>

                      {isCenterDropdownOpen && (
                        <div className="absolute right-0 mt-2 w-64 bg-background border rounded-md shadow-lg z-50 max-h-96 overflow-hidden">
                          <div className="p-2 border-b">
                            <input
                              type="text"
                              placeholder="센터 검색..."
                              value={centerSearchTerm}
                              onChange={(e) =>
                                setCenterSearchTerm(e.target.value)
                              }
                              className="w-full px-2 py-1 text-sm border rounded"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                          <div className="max-h-80 overflow-y-auto">
                            {Object.entries(getFilteredCenters()).length ===
                            0 ? (
                              <div className="p-3 text-sm text-muted-foreground">
                                검색 결과가 없습니다
                              </div>
                            ) : (
                              Object.entries(getFilteredCenters()).map(
                                ([group, centers]) => (
                                  <div key={group}>
                                    <div className="px-3 py-1 text-xs font-medium text-muted-foreground bg-muted">
                                      {group}
                                    </div>
                                    {centers.map((center) => (
                                      <button
                                        key={center.id}
                                        className={`w-full px-3 py-1.5 text-sm text-left hover:bg-accent ${
                                          selectedCenterId === center.id
                                            ? "bg-accent"
                                            : ""
                                        }`}
                                        onClick={() =>
                                          handleCenterChange(center.id)
                                        }
                                      >
                                        {center.label}
                                      </button>
                                    ))}
                                  </div>
                                )
                              )
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Center Info for other users */}
              {user?.role !== UserRole.SYSTEM_ADMIN && (
                <div className="text-sm text-muted-foreground">
                  {centersLoading ? (
                    <span>로딩 중...</span>
                  ) : (
                    <span>
                      {(() => {
                        const currentCenterId =
                          localStorage.getItem("centerId") || user?.centerId;
                        const currentCenter = allCenters.find(
                          (center) => center.id === currentCenterId
                        );
                        return currentCenter
                          ? currentCenter.label
                          : centerInfo?.centerName || "센터 정보 없음";
                      })()}
                    </span>
                  )}
                </div>
              )}

              {/* Theme Toggle */}
              <div className="relative" ref={themeMenuRef}>
                <button
                  className="p-2 rounded-md hover:bg-accent"
                  onClick={() => setThemeMenuOpen(!themeMenuOpen)}
                  title="테마 선택"
                >
                  {actualMode === "dark" ? (
                    <Moon size={20} />
                  ) : (
                    <Sun size={20} />
                  )}
                </button>

                {themeMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-background border rounded-md shadow-lg z-50">
                    <button
                      className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent ${
                        mode === "light" ? "bg-accent" : ""
                      }`}
                      onClick={() => {
                        setMode("light");
                        setThemeMenuOpen(false);
                      }}
                    >
                      <Sun size={16} />
                      라이트 모드
                    </button>
                    <button
                      className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent ${
                        mode === "dark" ? "bg-accent" : ""
                      }`}
                      onClick={() => {
                        setMode("dark");
                        setThemeMenuOpen(false);
                      }}
                    >
                      <Moon size={16} />
                      다크 모드
                    </button>
                    <button
                      className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent ${
                        mode === "system" ? "bg-accent" : ""
                      }`}
                      onClick={() => {
                        setMode("system");
                        setThemeMenuOpen(false);
                      }}
                    >
                      <Monitor size={16} />
                      시스템 설정
                    </button>
                  </div>
                )}
              </div>

              {/* User Menu */}
              <div className="relative" ref={userMenuRef}>
                <button
                  className="p-2 rounded-md hover:bg-accent"
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  title={`${user?.name} - 사용자 메뉴`}
                >
                  <User size={20} />
                </button>

                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-background border rounded-md shadow-lg z-50">
                    <button
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent"
                      onClick={handleProfile}
                    >
                      <User size={16} />
                      프로필
                    </button>
                    <button
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent"
                      onClick={handleLogout}
                    >
                      <LogOut size={16} />
                      로그아웃
                    </button>
                  </div>
                )}
              </div>
            </div>
          </header>

          {/* Main content area */}
          <main className="flex-1 p-6">
            <Routes>
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Navigate to="/dashboard" replace />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/survey-list"
                element={
                  <ProtectedRoute>
                    <SurveyList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/survey-builder"
                element={
                  <RoleProtectedRoute
                    allowedRoles={[
                      UserRole.SYSTEM_ADMIN,
                      UserRole.GENERAL_ADMIN,
                    ]}
                  >
                    <SurveyBuilder />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="/templates"
                element={
                  <RoleProtectedRoute
                    allowedRoles={[
                      UserRole.SYSTEM_ADMIN,
                      UserRole.GENERAL_ADMIN,
                    ]}
                  >
                    <TemplatePage />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="/create"
                element={
                  <RoleProtectedRoute
                    allowedRoles={[
                      UserRole.SYSTEM_ADMIN,
                      UserRole.GENERAL_ADMIN,
                    ]}
                  >
                    <SurveyBuilder />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="/edit/:id"
                element={
                  <RoleProtectedRoute
                    allowedRoles={[
                      UserRole.SYSTEM_ADMIN,
                      UserRole.GENERAL_ADMIN,
                    ]}
                  >
                    <SurveyBuilder />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="/survey-responses"
                element={
                  <RoleProtectedRoute
                    allowedRoles={[
                      UserRole.SYSTEM_ADMIN,
                      UserRole.GENERAL_ADMIN,
                    ]}
                  >
                    <SurveyResponses />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="/analytics"
                element={
                  <RoleProtectedRoute
                    allowedRoles={[
                      UserRole.SYSTEM_ADMIN,
                      UserRole.GENERAL_ADMIN,
                    ]}
                  >
                    <SurveyAnalytics />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="/analytics/:id"
                element={
                  <RoleProtectedRoute
                    allowedRoles={[
                      UserRole.SYSTEM_ADMIN,
                      UserRole.GENERAL_ADMIN,
                    ]}
                  >
                    <SurveyAnalytics />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="/statistics/dashboard"
                element={
                  <RoleProtectedRoute
                    allowedRoles={[
                      UserRole.SYSTEM_ADMIN,
                      UserRole.GENERAL_ADMIN,
                    ]}
                  >
                    <Statistics />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="/statistics/data"
                element={
                  <RoleProtectedRoute
                    allowedRoles={[
                      UserRole.SYSTEM_ADMIN,
                      UserRole.GENERAL_ADMIN,
                    ]}
                  >
                    <StatisticsData />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="/statistics"
                element={
                  <Navigate to="/statistics/dashboard" replace />
                }
              />
              <Route
                path="/user-management"
                element={
                  <RoleProtectedRoute allowedRoles={[UserRole.SYSTEM_ADMIN]}>
                    <UserManagement />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="/management/survey-types"
                element={
                  <RoleProtectedRoute
                    allowedRoles={[
                      UserRole.SYSTEM_ADMIN,
                      UserRole.GENERAL_ADMIN,
                    ]}
                  >
                    <SurveyTypeManagement />
                  </RoleProtectedRoute>
                }
              />
            </Routes>
          </main>
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
}

function App() {
  useEffect(() => {
    // Check environment variables on app start
    logConfiguration();
    const isValid = validateEnvironment();

    if (!isValid) {
      console.error(
        "Please ensure .env file is in the root directory and restart the development server."
      );
    }

    // Make test functions available in console for debugging
    (window as any).testAPIHeaders = testAPIHeaders;
    (window as any).browserEnvironmentTest = browserEnvironmentTest;
    (window as any).directEnvCheck = directCheck;
    (window as any).ENV = ENV;

    // Direct check of environment variables
    console.log("🔍 Direct ENV check on app start:");
    console.log(
      "  ACCESS_KEY:",
      ENV.ACCESS_KEY ? `Loaded (${ENV.ACCESS_KEY.length} chars)` : "NOT LOADED"
    );
    console.log(
      "  ACTION_KEY:",
      ENV.ACTION_KEY ? `Loaded (${ENV.ACTION_KEY.length} chars)` : "NOT LOADED"
    );

    console.log("💡 Debug commands:");
    console.log("  - directEnvCheck() for direct environment check");
    console.log("  - browserEnvironmentTest() for full environment test");
    console.log("  - testAPIHeaders() to test API configuration");
    console.log("  - ENV to see the environment object");
  }, []);

  return (
    <ThemeProvider>
      <AuthProvider>
        <SurveyProvider>
          <Router>
            <AppContent />
          </Router>
        </SurveyProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
